import { Box, Alert, useTheme } from '@mui/material';
import { useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { AlertColor } from '@mui/material/Alert';
import type { SxProps, Theme } from '@mui/material';
import { alertComponentStyles } from './AlertComponent.styles';
import { getAlerts, subscribe } from './AlertService';
import type { AlertEntry, AlertKindValue, AlertTypeValue } from './AlertService';

const FULL_PAGE_ALERT_HOST_SELECTOR = '.global-alert-messages';

const severityMap: Record<AlertTypeValue, AlertColor> = {
  ERROR: 'error',
  SUCCESS: 'success',
  INFO: 'info',
};

type AlertPlacement = 'inline' | 'full-page';

interface AlertComponentProps {
  alertSx?: SxProps<Theme>;
  hideIcon?: boolean;
  kind?: AlertKindValue;
  onFullPageVisibilityChange?: (isVisible: boolean) => void;
  placement?: AlertPlacement;
}

function mergeSx(baseSx: SxProps<Theme>, overrideSx?: SxProps<Theme>): SxProps<Theme> {
  if (!overrideSx) {
    return baseSx;
  }

  return [baseSx, overrideSx] as SxProps<Theme>;
}

export const AlertComponent = ({
  alertSx,
  hideIcon = false,
  kind,
  onFullPageVisibilityChange,
  placement = 'inline',
}: AlertComponentProps = {}) => {
  const theme = useTheme();
  const [alerts, setAlerts] = useState<AlertEntry[]>(() => getAlerts());

  useEffect(() => {
    const unsubscribe = subscribe(() => {
      setAlerts(getAlerts());
    });
    return unsubscribe;
  }, []);

  const visibleAlerts = kind
    ? alerts.filter((entry) => entry.kind === kind)
    : alerts;
  const fullPageHost = placement === 'full-page'
    ? document.querySelector<HTMLElement>(FULL_PAGE_ALERT_HOST_SELECTOR)
    : null;
  const isFullPageVisible = Boolean(fullPageHost) && visibleAlerts.length > 0;

  useLayoutEffect(() => {
    onFullPageVisibilityChange?.(isFullPageVisible);
  }, [isFullPageVisible, onFullPageVisibilityChange]);

  if (visibleAlerts.length === 0) {
    return null;
  }

  const alertList = (
    <Box
      sx={fullPageHost
        ? mergeSx(
          alertComponentStyles.container(theme),
          alertComponentStyles.fullPageContainer()
        )
        : alertComponentStyles.container(theme)}
    >
      {visibleAlerts.map((entry) => (
        <Alert
          key={entry.id}
          severity={severityMap[entry.type]}
          icon={hideIcon ? false : undefined}
          onClose={entry.close}
          sx={mergeSx(alertComponentStyles.alert(theme), alertSx)}
        >
          {entry.message}
        </Alert>
      ))}
    </Box>
  );

  return fullPageHost ? createPortal(alertList, fullPageHost) : alertList;
};

export default AlertComponent;
